import React, { useCallback, useMemo, useRef, type ComponentType } from 'react';
import { useFragment, graphql } from 'react-relay';
import { useRegisterNudgeTarget } from '@atlassiansox/nudge-tooltip';
import { Box, xcss } from '@atlaskit/primitives';
import type { CSSToken } from '@atlaskit/tokens';
import { useInlineCreateHandlers } from '@atlassian/jira-issue-table-inline-issue-create/src/ui/index.tsx';
import InlineCreateForm, {
	type Props as InlineCreateFormProps,
} from '@atlassian/jira-issue-table-inline-issue-create/src/ui/inline-create-form/index.tsx';
import InlineCreateTrigger from '@atlassian/jira-issue-table-inline-issue-create/src/ui/inline-create-trigger/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import type { ContextualFields } from '@atlassian/jira-issue-table-inline-issue-create/src/common/types.tsx';
import { GetStartedListNudge } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/list-nudge-tour/get-started-list/async.tsx';
import { GET_STARTED_LIST_NUDGE_ID } from '@atlassian/jira-onboarding-core/src/constants.tsx';
import type {
	BorderWidth,
	FooterProps,
	RankFieldTerms,
	TopLevelIssueCreateConfig,
} from '../../../common/types.tsx';
import { Footer as DefaultFooter } from '../../../common/ui/components/footer/index.tsx';
import {
	useIsIssueRankDescending,
	useIsIssueRankingEnabled,
	useIsProjectQuickstartOnboardingEnabled,
	useIsTimelineEnabled,
	useJql,
	useProjectContext,
} from '../../../controllers/features/selectors.tsx';
import { useCalculateIssueInsertionPoint } from '../../../controllers/table-items-intersection-observer/index.tsx';
import { useIssueCreateMutation } from '../../../services/issue-create-mutation/CreateIssueController.tsx';
import type { IssueCreateFooter$key } from './__generated__/IssueCreateFooter.graphql';
import type { IssueCreateFooter_rankField$key as RankFieldFragment } from './__generated__/IssueCreateFooter_rankField.graphql';

export type Props = FooterProps & {
	Footer?: ComponentType<FooterProps>;
	issues: IssueCreateFooter$key;
	rankField: RankFieldFragment | null;
	// TODO: Make this required when refactor_issue_table_for_timline is being cleaned up
	canShowAdditionalFields?: boolean;
	/**
	 * The border radius of the inline create footer.
	 */
	footerBorderRadius?: CSSToken;
	/**
	 * Configuration to limit the allowed issue types users can create at the top level.
	 */
	topLevelIssueCreateConfig?: TopLevelIssueCreateConfig;
};

type FormWithInsertionPointProps = Omit<InlineCreateFormProps, 'onCreateIssue'> & {
	connectionId: string;
	rankFieldTerms: RankFieldTerms;
};

const FormWithInsertionPoint = ({
	connectionId,
	rankFieldId,
	rankFieldTerms,
	footerBorderRadius,
	...props
}: FormWithInsertionPointProps) => {
	const insertionPoint = useCalculateIssueInsertionPoint();

	let jql: string | undefined;
	if (expVal('jira_spreadsheet_issue_create_in_group', 'isEnabled', false)) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		jql = useJql();
	}

	const handleIssueCreate = useIssueCreateMutation({
		issueConnectionId: connectionId,
		jql,
		...(fg('jsc_list_reranking_gate') && { rankFieldTerms }),
		...(fg('jira_timeline_analytics_harmonization_v2') && { location: 'footerCreateForm' }),
	});

	const isIssueRankDescending = useIsIssueRankDescending();
	const isIssueRankingEnabled = useIsIssueRankingEnabled();
	const isTimelineEnabled = useIsTimelineEnabled();

	const formContextualFields = useMemo<ContextualFields | undefined>(() => {
		if (!isIssueRankingEnabled || !fg('jsc_list_reranking_gate')) {
			return undefined;
		}
		if (isIssueRankDescending) {
			return insertionPoint?.position === 'below'
				? { rankBeforeIssueId: insertionPoint?.issueId, rankFieldId }
				: { rankAfterIssueId: insertionPoint?.issueId, rankFieldId };
		}
		return insertionPoint?.position === 'below'
			? { rankAfterIssueId: insertionPoint?.issueId, rankFieldId }
			: { rankBeforeIssueId: insertionPoint?.issueId, rankFieldId };
	}, [
		insertionPoint?.issueId,
		insertionPoint?.position,
		isIssueRankDescending,
		isIssueRankingEnabled,
		rankFieldId,
	]);

	const handleIssueCreateWithInsertionPoint = useCallback<InlineCreateFormProps['onCreateIssue']>(
		(issueToCreate, contextualFields) => {
			if (insertionPoint == null) {
				return handleIssueCreate(issueToCreate, contextualFields);
			}

			return handleIssueCreate(issueToCreate, contextualFields, {
				insertAfter: insertionPoint.position === 'below',
				insertNodeId: insertionPoint.issueId,
			});
		},
		[handleIssueCreate, insertionPoint],
	);

	return (
		<Box xcss={formContainerStyles}>
			<InlineCreateForm
				{...props}
				onCreateIssue={handleIssueCreateWithInsertionPoint}
				rankFieldId={rankFieldId}
				contextualFields={formContextualFields}
				isTimeline={isTimelineEnabled}
				footerBorderRadius={footerBorderRadius}
			/>
		</Box>
	);
};

const nudgeRuntimeProps = {};

export const IssueCreateFooter = ({
	// @ts-expect-error: to unblock React 18.2.0 -> 18.3.1 version bump
	Footer = DefaultFooter,
	issues,
	rankField,
	canShowAdditionalFields,
	footerBorderRadius,
	topLevelIssueCreateConfig,
	...footerProps
}: Props) => {
	const issueData = useFragment<IssueCreateFooter$key>(
		graphql`
			fragment IssueCreateFooter on JiraIssueConnection {
				__id
			}
		`,
		issues,
	);

	const rankFieldData = useFragment<RankFieldFragment>(
		graphql`
			fragment IssueCreateFooter_rankField on JiraJqlFieldWithAliases
			@argumentDefinitions(
				isJSCAliasChangesEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jsc-alias-changes.relayprovider"
				}
			) {
				fieldId
				jqlTerm
				aliases @include(if: $isJSCAliasChangesEnabled)
			}
		`,
		rankField ?? null,
	);

	const projectContext = useProjectContext();
	const isFormRendered = useRef(false);
	const inlineCreateHandlersInput = fg('timeline_harmonisation_m3_targeting')
		? {
				projectKey: projectContext,
				maxHierarchyLevel: topLevelIssueCreateConfig?.maxHierarchyLevel,
				minHierarchyLevel: topLevelIssueCreateConfig?.minHierarchyLevel ?? 0,
			}
		: {
				projectKey: projectContext,
				minHierarchyLevel: 0,
			};

	const { isFormVisible, formProps, triggerProps } =
		useInlineCreateHandlers(inlineCreateHandlersInput);
	const rankFieldTerms = useMemo<RankFieldTerms>(
		() => ({
			jqlTerm: rankFieldData?.jqlTerm ?? null,
			aliases: rankFieldData?.aliases ? Array.from(rankFieldData.aliases) : null,
		}),
		[rankFieldData],
	);

	const isProjectQuickstartOnboardingEnabled = useIsProjectQuickstartOnboardingEnabled();
	const isNudgeTargetEnabled = isProjectQuickstartOnboardingEnabled && fg('jira_list_nin_merge');
	const { ref: gettingStartedListNudgeRef } = useRegisterNudgeTarget<HTMLDivElement>(
		GET_STARTED_LIST_NUDGE_ID,
		isNudgeTargetEnabled,
	);

	const isTimelineEnabled = useIsTimelineEnabled();

	if (isFormVisible) {
		isFormRendered.current = true;
		return (
			<FormWithInsertionPoint
				connectionId={issueData?.__id}
				rankFieldId={rankFieldData?.fieldId ?? null}
				rankFieldTerms={rankFieldTerms}
				{...formProps}
				appearance="footer"
				depth={0}
				{...(fg('refactor_issue_table_for_timline')
					? { canShowAdditionalFields }
					: { canShowAdditionalFields: !isTimelineEnabled })}
				footerBorderRadius={footerBorderRadius}
			/>
		);
	}

	const footer = (
		<Footer
			ref={fg('jira_list_nin_merge') ? gettingStartedListNudgeRef : undefined}
			{...footerProps}
			start={
				<Box xcss={triggerContainerStyles}>
					<InlineCreateTrigger {...triggerProps} isFormRendered={isFormRendered.current} />
				</Box>
			}
		/>
	);

	if (isNudgeTargetEnabled) {
		return <GetStartedListNudge runtimeProps={nudgeRuntimeProps}>{footer}</GetStartedListNudge>;
	}

	return footer;
};

const BORDER_WIDTH: BorderWidth = '1px';
const formContainerStyles = xcss({
	backgroundColor: 'elevation.surface',
	boxShadow: 'elevation.shadow.overflow',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	borderTopWidth: BORDER_WIDTH,
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	borderTopStyle: 'solid',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	borderTopColor: 'color.border',
});

const triggerContainerStyles = xcss({
	display: 'flex',
	flex: 1,
	alignItems: 'center',
	minHeight: '32px',
});
